import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';

// ── Purchase orders ─────────────────────────────────────────────────────────
// Vendor orders with expected delivery dates. Status: draft → ordered →
// delivered; ordered POs with dates mark the planner and worker portals.

interface PoItem { label: string; value?: string }
interface Po {
  id: number; po_number: string; job_id?: number | null; job_title?: string | null;
  vendor_id?: number | null; vendor_name?: string | null; vendor_display?: string | null;
  items: PoItem[]; status: 'draft' | 'ordered' | 'delivered' | 'cancelled';
  expected_date?: string | null; expected_time?: string | null; total?: number | null; notes?: string | null;
  expense_id?: number | null; expense_invoice?: string | null; expense_total?: number | null;
  created_by_name?: string;
}
interface VendorOpt { id: number; name: string }
interface JobOpt { id: number; title: string }

const STATUS_CLS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  ordered: 'bg-blue-100 text-blue-700',
  delivered: 'bg-green-100 text-green-700',
};

export const PurchaseOrdersPage = () => {
  const navigate = useNavigate();
  const [pos, setPos] = useState<Po[]>([]);
  const [loading, setLoading] = useState(true);
  const [openPo, setOpenPo] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [vendors, setVendors] = useState<VendorOpt[]>([]);
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  // create form
  const [fVendor, setFVendor] = useState<number | ''>('');
  const [fVendorName, setFVendorName] = useState('');
  const [fJob, setFJob] = useState<number | ''>('');
  const [fDate, setFDate] = useState('');
  const [fTotal, setFTotal] = useState('');
  const [fNotes, setFNotes] = useState('');
  const [fItems, setFItems] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    apiClient.get('/purchase-orders').then(r => { setPos(r.data.data ?? []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => {
    load();
    apiClient.get('/vendors').then(r => {
      const d = r.data?.data ?? r.data;
      setVendors((Array.isArray(d) ? d : d?.vendors ?? []) as VendorOpt[]);
    }).catch(() => {});
    apiClient.get('/jobs?limit=200').then(r => {
      const d = r.data?.data ?? r.data;
      const list = (Array.isArray(d) ? d : (Array.isArray(d?.jobs) ? d.jobs : [])) as (JobOpt & { status?: string })[];
      setJobs(list.filter(j => !['completed', 'cancelled'].includes(String(j.status || '').toLowerCase())));
    }).catch(() => {});
  }, []);

  const create = async () => {
    const items: PoItem[] = fItems.split('\n').map(l => l.trim()).filter(Boolean)
      .map(l => {
        const m = l.match(/^(.*?)\s*[—|-]\s*([^—|-]+)$/);
        return m ? { label: m[1].trim(), value: m[2].trim() } : { label: l };
      });
    if (!items.length) return;
    setBusy(true);
    try {
      await apiClient.post('/purchase-orders', {
        vendor_id: fVendor || undefined,
        vendor_name: fVendor ? undefined : (fVendorName.trim() || undefined),
        job_id: fJob || undefined,
        expected_date: fDate || undefined,
        total: fTotal ? Number(fTotal) : undefined,
        notes: fNotes.trim() || undefined,
        items,
      });
      setCreating(false);
      setFVendor(''); setFVendorName(''); setFJob(''); setFDate(''); setFTotal(''); setFNotes(''); setFItems('');
      load();
    } finally { setBusy(false); }
  };

  const setStatus = async (po: Po, status: Po['status']) => {
    setPos(ps => ps.map(p => p.id === po.id ? { ...p, status } : p));
    try { await apiClient.put(`/purchase-orders/${po.id}`, { status }); } catch { load(); }
  };

  const setDate = async (po: Po, date: string) => {
    setPos(ps => ps.map(p => p.id === po.id ? { ...p, expected_date: date || null } : p));
    try { await apiClient.put(`/purchase-orders/${po.id}`, { expected_date: date || null }); } catch { load(); }
  };

  // CR #169 — delivery time + expense-invoice attribution
  const setTime = async (po: Po, time: string) => {
    setPos(ps => ps.map(p => p.id === po.id ? { ...p, expected_time: time || null } : p));
    try { await apiClient.put(`/purchase-orders/${po.id}`, { expected_time: time || null }); } catch { load(); }
  };
  const [expensePickerFor, setExpensePickerFor] = useState<Po | null>(null);
  const [pickerExpenses, setPickerExpenses] = useState<{ id: number; invoice_number?: string | null; vendor?: string | null; total?: number; expense_date?: string | null }[]>([]);
  const openExpensePicker = async (po: Po) => {
    if (!po.job_id) { alert('This PO has no job — attach it to a job first.'); return; }
    setExpensePickerFor(po);
    try {
      const r = await apiClient.get(`/jobs/${po.job_id}/expenses`);
      const d = r.data?.data;
      setPickerExpenses((Array.isArray(d) ? d : d?.expenses ?? []) as typeof pickerExpenses);
    } catch { setPickerExpenses([]); }
  };
  const attachExpense = async (expenseId: number | null) => {
    const po = expensePickerFor;
    setExpensePickerFor(null);
    if (!po) return;
    try {
      await apiClient.put(`/purchase-orders/${po.id}`, { expense_id: expenseId });
      load();
    } catch { load(); }
  };

  const field = 'border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30';

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Purchase Orders"
        subtitle="Vendor orders with delivery dates — ordered POs mark the planner and crew portals"
        actions={
          <button onClick={() => setCreating(true)}
            className="flex items-center gap-1 rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90">
            <span className="material-symbols-outlined text-base">add</span> New PO
          </button>
        }
      />

      {creating && (
        <div className="bg-white rounded-xl border border-[#C77D3F]/40 p-4 space-y-2.5">
          <div className="flex gap-2 flex-wrap">
            <select value={fVendor} onChange={e => setFVendor(e.target.value ? Number(e.target.value) : '')} className={`${field} bg-white`}>
              <option value="">Vendor (from list)…</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {!fVendor && (
              <input value={fVendorName} onChange={e => setFVendorName(e.target.value)} placeholder="…or type a vendor" className={field} />
            )}
            <select value={fJob} onChange={e => setFJob(e.target.value ? Number(e.target.value) : '')} className={`${field} bg-white`}>
              <option value="">Job (optional)…</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
              Expected
              <input type="date" value={fDate} onChange={e => setFDate(e.target.value)} className={field} />
            </label>
            <input value={fTotal} onChange={e => setFTotal(e.target.value)} inputMode="decimal" placeholder="Total $ (opt.)" className={`${field} w-28`} />
          </div>
          <textarea value={fItems} onChange={e => setFItems(e.target.value)} rows={5}
            placeholder={'One item per line — "24× 2x6 SPF — 12\'"\nPaste straight from a calculator cut list or AI takeoff.'}
            className={`${field} w-full resize-none font-mono text-xs`} />
          <input value={fNotes} onChange={e => setFNotes(e.target.value)} placeholder="Notes (delivery instructions, PO terms…)" className={`${field} w-full`} />
          <div className="flex gap-2">
            <button onClick={() => setCreating(false)} className="h-10 px-4 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
            <button onClick={create} disabled={busy || !fItems.trim()}
              className="h-10 px-5 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
              {busy ? 'Creating…' : 'Create PO'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl block mb-3">local_shipping</span>
            <p className="text-sm text-gray-500">No purchase orders yet — turn a cut list into one.</p>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
                <th className="px-4 py-2.5 font-semibold">PO</th>
                <th className="px-3 py-2.5 font-semibold">Vendor</th>
                <th className="px-3 py-2.5 font-semibold">Job</th>
                <th className="px-3 py-2.5 font-semibold">Expected</th>
                <th className="px-3 py-2.5 font-semibold text-right">Total</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pos.map(po => (
                <>
                  <tr key={po.id} onClick={() => setOpenPo(o => o === po.id ? null : po.id)}
                    className={`cursor-pointer hover:bg-gray-50 ${po.status === 'delivered' ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3 font-bold text-primary whitespace-nowrap">{po.po_number}</td>
                    <td className="px-3 py-3 text-gray-700">{po.vendor_display || po.vendor_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-600">
                      {po.job_id
                        ? <button onClick={e => { e.stopPropagation(); navigate(`/jobs/${po.job_id}`); }} className="hover:text-primary">{po.job_title}</button>
                        : '—'}
                    </td>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <input type="date" value={(po.expected_date || '').slice(0, 10)}
                          onChange={e => setDate(po, e.target.value)}
                          className="border border-gray-200 rounded-md px-1.5 py-1 text-xs text-gray-600 focus:outline-none" />
                        {/* CR #169 — delivery time of day */}
                        <input type="time" value={(po.expected_time || '').slice(0, 5)}
                          onChange={e => setTime(po, e.target.value)}
                          title="Delivery time"
                          className="border border-gray-200 rounded-md px-1.5 py-1 text-xs text-gray-600 focus:outline-none w-[84px]" />
                      </div>
                      {/* CR #169 — which expense invoice paid for this delivery */}
                      <button onClick={() => openExpensePicker(po)}
                        title="Attribute this delivery to an expense invoice"
                        className={`mt-1 text-[11px] flex items-center gap-0.5 ${po.expense_id ? 'text-green-700 font-semibold' : 'text-gray-400 hover:text-[#C77D3F]'}`}>
                        <span className="material-symbols-outlined text-[13px]">receipt_long</span>
                        {po.expense_id ? (po.expense_invoice || `expense #${po.expense_id}`) : 'Link invoice'}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      {po.total != null ? `$${Number(po.total).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CLS[po.status] || 'bg-gray-100 text-gray-500'}`}>{po.status}</span>
                    </td>
                    <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {po.status === 'draft' && (
                          <button onClick={() => setStatus(po, 'ordered')}
                            className="h-7 px-2.5 rounded-lg bg-blue-600 text-white text-[11px] font-bold">Mark ordered</button>
                        )}
                        {po.status === 'ordered' && (
                          <button onClick={() => setStatus(po, 'delivered')}
                            className="h-7 px-2.5 rounded-lg bg-green-600 text-white text-[11px] font-bold">Delivered</button>
                        )}
                        <button onClick={() => { if (window.confirm(`Cancel ${po.po_number}?`)) setStatus(po, 'cancelled'); }}
                          title="Cancel PO"
                          className="h-7 w-7 rounded-lg text-gray-300 hover:text-red-600 flex items-center justify-center">
                          <span className="material-symbols-outlined text-base">close</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {openPo === po.id && (
                    <tr key={`${po.id}-d`}>
                      <td colSpan={7} className="bg-gray-50 px-4 py-2.5">
                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                          {po.items.map((it, i) => (
                            <div key={i} className="flex justify-between text-xs">
                              <span className="text-gray-700">{it.label}</span>
                              <span className="font-semibold text-gray-900 whitespace-nowrap ml-2">{it.value || ''}</span>
                            </div>
                          ))}
                        </div>
                        {po.notes && <p className="text-[11px] text-gray-500 italic mt-1.5">"{po.notes}"</p>}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CR #169 — pick the expense invoice this delivery belongs to */}
      {expensePickerFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setExpensePickerFor(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <span className="material-symbols-outlined text-[#C77D3F]">receipt_long</span>
              <p className="font-bold text-gray-900 flex-1">Link {expensePickerFor.po_number} to an expense invoice</p>
              <button onClick={() => setExpensePickerFor(null)} className="text-gray-400 hover:text-gray-700">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="overflow-y-auto divide-y divide-gray-50">
              {expensePickerFor.expense_id != null && (
                <button onClick={() => attachExpense(null)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-red-50 text-red-600 text-sm font-semibold">
                  <span className="material-symbols-outlined">link_off</span> Unlink current invoice
                </button>
              )}
              {pickerExpenses.length === 0 && (
                <p className="text-sm text-gray-400 p-6 text-center">No expenses on this job yet — add the receipt in the job's Expenses tab first.</p>
              )}
              {pickerExpenses.map(ex => (
                <button key={ex.id} onClick={() => attachExpense(ex.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50">
                  <span className="material-symbols-outlined text-gray-400">receipt</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">
                      {ex.invoice_number || 'No invoice #'}{ex.vendor ? ` — ${ex.vendor}` : ''}
                    </span>
                    <span className="block text-xs text-gray-400">
                      {ex.expense_date ? String(ex.expense_date).slice(0, 10) : ''}
                      {ex.total != null ? ` · $${Number(ex.total).toLocaleString('en-CA', { minimumFractionDigits: 2 })}` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};
